import { __assign, __read, __rest } from "tslib";
import React, { useState, useRef } from "react";
import classNames from "classnames";
import { Button } from "../button";
import { mergeRefs } from "../_util/merge-refs";
import { useConfig } from "../_util/config-context";
import { useDefaultValue } from "../form/controlled";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { List } from "../list";
import { Icon } from "../icon";
import { Input } from "./Input";
import { Bubble } from "../bubble";
import { useTranslation } from "../i18n";
function getDefaultRules(t) {
    return [
        {
            text: t.passwordLengthRule,
            validator: function (value) { return value.length >= 8 && value.length <= 30; },
        },
        {
            text: t.passwordNoSlashStartRule,
            validator: function (value) { return !value.startsWith("/"); },
        },
        {
            text: t.passwordAtLeastThreeRule,
            validator: function (_, results) {
                return results.reduce(function (pre, cur) { return (cur ? pre + 1 : pre); }, 0) >= 3;
            },
            subRules: [
                {
                    text: t.passwordLowercaseRule,
                    validator: function (value) { return /[a-z]/.test(value); },
                },
                {
                    text: t.passwordUppercaseRule,
                    validator: function (value) { return /[A-Z]/.test(value); },
                },
                {
                    text: t.passwordNumberRule,
                    validator: function (value) { return /[0-9]/.test(value); },
                },
                {
                    text: t.passwordSpecialCharRule,
                    validator: function (value) { return /[()!@#$%^&*|?><]/.test(value); },
                },
            ],
        },
    ];
}
function isValid(value, rules) {
    return (rules || []).reduce(function (pre, cur) {
        var subResults = Array.isArray(cur.subRules)
            ? cur.subRules.map(function (_a) {
                var validator = _a.validator;
                return validator(value);
            })
            : [];
        return pre && cur.validator(value, subResults);
    }, true);
}
export var InputPassword = forwardRefWithStatics(function (props, ref) {
    var classPrefix = useConfig().classPrefix;
    var t = useTranslation();
    var _a = useDefaultValue(props, ""), value = _a.value, onChange = _a.onChange, className = _a.className, style = _a.style, _b = _a.rules, rules = _b === void 0 ? getDefaultRules(t) : _b, _c = _a.bubblePlacement, bubblePlacement = _c === void 0 ? "right-start" : _c, size = _a.size, readonly = _a.readonly, disabled = _a.disabled, inputProps = __rest(_a, ["value", "onChange", "className", "style", "rules", "bubblePlacement", "size", "readonly", "disabled"]);
    var inputRef = useRef(null);
    var _d = __read(useState(false), 2), show = _d[0], setShow = _d[1];
    return (React.createElement("div", { className: classNames(classPrefix + "-input-password", className, {
            "size-full-width": size === "full",
        }), style: style },
        React.createElement(Bubble, { trigger: "focus", arrowPointAtCenter: true, placement: bubblePlacement, className: classPrefix + "-input-password-bubble", content: Array.isArray(rules) &&
                rules.length && (React.createElement(List, null, rules.map(function (_a, index) {
                var text = _a.text, validator = _a.validator, subRules = _a.subRules;
                return (React.createElement(List.Item, { key: index },
                    React.createElement(Icon, { type: validator(value, Array.isArray(subRules)
                            ? subRules.map(function (_a) {
                                var validator = _a.validator;
                                return validator(value);
                            })
                            : [])
                            ? "success"
                            : "error--ring" }),
                    text,
                    Array.isArray(subRules) && (React.createElement(List, { type: "bullet" }, subRules.map(function (_a, index) {
                        var text = _a.text, validator = _a.validator;
                        return (React.createElement(List.Item, { key: index, className: classNames({
                                "is-success": validator(value),
                            }) }, text));
                    })))));
            }))) },
            React.createElement(Input, __assign({}, inputProps, { size: size, value: value, onChange: function (value, _a) {
                    var event = _a.event;
                    return onChange(value, { event: event, valid: isValid(value, rules) });
                }, ref: mergeRefs(ref, inputRef), type: show ? "text" : "password", readonly: readonly, disabled: disabled }))),
        React.createElement("div", { className: classPrefix + "-input-password__op" },
            !readonly && !disabled && value && (React.createElement(Button, { htmlType: "button", type: "icon", icon: "close", tabIndex: -1, onClick: function (event) {
                    return onChange("", { event: event, valid: isValid("", rules) });
                }, onMouseUp: function (e) { return e.preventDefault(); }, onMouseDown: function (e) { return e.preventDefault(); } })),
            React.createElement(Button, { htmlType: "button", type: "icon", tabIndex: -1, icon: show ? "show" : "hide", onClick: function () { return setShow(function (show) { return !show; }); }, onMouseUp: function (e) { return e.preventDefault(); }, onMouseDown: function (e) { return e.preventDefault(); } }))));
}, {
    defaultLabelAlign: "middle",
});
InputPassword.displayName = "InputPassword";
//# sourceMappingURL=InputPassword.js.map